import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  orderBy,
  where,
  limit,
  startAfter,
  Timestamp,
  QueryConstraint,
  QueryDocumentSnapshot,
  DocumentData,
} from "firebase/firestore";
import { db } from "./init";
import { Transaction } from "@/types/transaction";
import { convertTimestampToDate } from "./firestore";

/**
 * Firestore 交易數據類型（包含 Timestamp）
 */
interface FirestoreTransactionData {
  type?: string;
  user_id?: string;
  amount?: number;
  transaction_id?: string;
  video_id?: string | null;
  drama_id?: string | null;
  drama_title?: string | null;
  episode?: number | null;
  company_id?: string | null;
  company_name?: string | null;
  product_id?: string | null;
  platform?: string | null;
  receipt_data?: string | null;
  description?: string | null;
  created_at?: Timestamp | Date | null;
  revenue_share_rate?: number;
  base_cost?: number;
  settlement_status?: string;
  settlement_date?: Timestamp | Date | null;
}

/**
 * 轉換交易數據（處理 Timestamp）
 */
function convertTransactionData(
  data: FirestoreTransactionData,
  id: string
): Omit<Transaction, "id"> {
  return {
    type: (data.type as Transaction["type"]) || "adjustment",
    user_id: data.user_id || "",
    amount: data.amount || 0,
    transaction_id: data.transaction_id || "",
    video_id: data.video_id,
    drama_id: data.drama_id,
    drama_title: data.drama_title,
    episode: data.episode,
    company_id: data.company_id,
    company_name: data.company_name,
    product_id: data.product_id,
    platform: (data.platform as Transaction["platform"]) || null,
    receipt_data: data.receipt_data,
    description: data.description,
    created_at: convertTimestampToDate(data.created_at) as Date | undefined,
    revenue_share_rate: data.revenue_share_rate,
    base_cost: data.base_cost,
    settlement_status: data.settlement_status as
      | "pending"
      | "processing"
      | "settled"
      | undefined,
    settlement_date: convertTimestampToDate(
      data.settlement_date
    ) as Date | undefined,
  };
}

/**
 * 交易列表查詢選項
 */
export interface TransactionListOptions {
  filters?: {
    type?: Transaction["type"];
    types?: Transaction["type"][];
    drama_id?: string;
    user_id?: string;
    startDate?: Date;
    endDate?: Date;
  };
  limit?: number;
  startAfter?: QueryDocumentSnapshot<DocumentData>;
}

/**
 * 交易列表查詢結果
 */
export interface TransactionListResult {
  transactions: Transaction[];
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
}

/**
 * 獲取交易記錄（支援分頁）
 */
export async function getTransactions(
  options: TransactionListOptions = {}
): Promise<TransactionListResult> {
  try {
    const transactionsRef = collection(db, "transactions");
    const constraints: QueryConstraint[] = [];
    const filters = options.filters || {};
    const pageSize = options.limit || 50;

    // 添加篩選條件（drama_id 和 type 順序需符合複合索引）
    if (filters.drama_id) {
      constraints.push(where("drama_id", "==", filters.drama_id));
    }
    if (filters.types && filters.types.length > 0) {
      if (filters.types.length === 1) {
        constraints.push(where("type", "==", filters.types[0]));
      } else {
        constraints.push(where("type", "in", filters.types));
      }
    } else if (filters.type) {
      constraints.push(where("type", "==", filters.type));
    }
    if (filters.user_id) {
      constraints.push(where("user_id", "==", filters.user_id));
    }
    if (filters.startDate) {
      constraints.push(
        where("created_at", ">=", Timestamp.fromDate(filters.startDate))
      );
    }
    if (filters.endDate) {
      // 結束日期需要包含當天的結束時間（23:59:59）
      const endDate = new Date(filters.endDate);
      endDate.setHours(23, 59, 59, 999);
      constraints.push(
        where("created_at", "<=", Timestamp.fromDate(endDate))
      );
    }

    // 排序
    constraints.push(orderBy("created_at", "desc"));

    // 分頁
    if (options.startAfter) {
      constraints.push(startAfter(options.startAfter));
    }

    // 限制數量（多取一筆以判斷是否有更多資料）
    constraints.push(limit(pageSize + 1));

    const q = query(transactionsRef, ...constraints);
    const querySnapshot = await getDocs(q);

    const docs = querySnapshot.docs;
    const hasMore = docs.length > pageSize;
    const transactions = (hasMore ? docs.slice(0, pageSize) : docs).map(
      (doc) => ({
        id: doc.id,
        ...convertTransactionData(doc.data(), doc.id),
      })
    ) as Transaction[];

    return {
      transactions,
      lastDoc: hasMore ? docs[pageSize - 1] : null,
      hasMore,
    };
  } catch (error) {
    console.error("Error getting transactions:", error);
    throw error;
  }
}

/**
 * 匯出用：依目前篩選條件抓取全部交易
 * 以分頁迴圈抓取，抓到 hasMore=false 或 hitCap 就停止
 * 預設上限 10,000 筆，避免瀏覽器記憶體爆掉
 */
export async function fetchAllTransactionsForExport(
  filters: TransactionListOptions["filters"] = {},
  options: { batchSize?: number; maxRecords?: number } = {}
): Promise<{ transactions: Transaction[]; truncated: boolean; total: number }> {
  const batchSize = options.batchSize ?? 500;
  const maxRecords = options.maxRecords ?? 10000;

  const all: Transaction[] = [];
  let cursor: QueryDocumentSnapshot<DocumentData> | undefined = undefined;
  let hasMore = true;

  while (hasMore && all.length < maxRecords) {
    const limit = Math.min(batchSize, maxRecords - all.length);
    const result = await getTransactions({
      filters,
      limit,
      startAfter: cursor,
    });
    all.push(...result.transactions);
    hasMore = result.hasMore;
    cursor = result.lastDoc ?? undefined;
    if (!cursor) break;
  }

  return {
    transactions: all,
    truncated: hasMore && all.length >= maxRecords,
    total: all.length,
  };
}

/**
 * 搜尋交易記錄（支援多種搜尋條件）
 */
export async function searchTransactions(
  searchQuery: string
): Promise<Transaction[]> {
  try {
    // 如果搜尋條件為空，返回空陣列
    if (!searchQuery.trim()) {
      return [];
    }

    const transactionsRef = collection(db, "transactions");
    const results: Transaction[] = [];
    const seenIds = new Set<string>();

    // 嘗試按 user_id 搜尋
    try {
      const userQuery = query(
        transactionsRef,
        where("user_id", "==", searchQuery.trim())
      );
      const userSnapshot = await getDocs(userQuery);
      userSnapshot.docs.forEach((doc) => {
        if (!seenIds.has(doc.id)) {
          seenIds.add(doc.id);
          results.push({
            id: doc.id,
            ...convertTransactionData(doc.data(), doc.id),
          } as Transaction);
        }
      });
    } catch (error) {
      // 忽略查詢錯誤
    }

    // 嘗試按 transaction_id 搜尋
    try {
      const transactionIdQuery = query(
        transactionsRef,
        where("transaction_id", "==", searchQuery.trim())
      );
      const transactionIdSnapshot = await getDocs(transactionIdQuery);
      transactionIdSnapshot.docs.forEach((doc) => {
        if (!seenIds.has(doc.id)) {
          seenIds.add(doc.id);
          results.push({
            id: doc.id,
            ...convertTransactionData(doc.data(), doc.id),
          } as Transaction);
        }
      });
    } catch (error) {
      // 忽略查詢錯誤
    }

    // 按時間排序
    results.sort((a, b) => {
      const aTime = a.created_at?.getTime() || 0;
      const bTime = b.created_at?.getTime() || 0;
      return bTime - aTime; // 降序
    });

    return results;
  } catch (error) {
    console.error("Error searching transactions:", error);
    throw error;
  }
}

/**
 * 劇集每集付費統計
 */
export interface DramaEpisodeStats {
  episodeUnlockCounts: Record<number, number>; // episode -> unique user count
  fullDramaUnlockCount: number;
  totalUnlockRevenue: number; // 解鎖相關交易的金幣總計（負數 amount 的絕對值）
}

/**
 * 獲取劇集每集付費人數與全劇解鎖人數
 */
export async function getDramaEpisodeStats(
  dramaId: string
): Promise<DramaEpisodeStats> {
  try {
    const transactionsRef = collection(db, "transactions");
    const dramaTypes: Transaction["type"][] = [
      "unlock",
      "full_drama_coin_purchase",
    ];
    const q = query(
      transactionsRef,
      where("drama_id", "==", dramaId),
      where("type", "in", dramaTypes),
      orderBy("created_at", "desc"),
      limit(5000)
    );
    const snapshot = await getDocs(q);

    const episodeUsers = new Map<number, Set<string>>();
    const fullDramaUsers = new Set<string>();
    let totalUnlockRevenue = 0;

    snapshot.docs.forEach((docSnap) => {
      const data = convertTransactionData(docSnap.data(), docSnap.id);
      const tx = { id: docSnap.id, ...data } as Transaction;

      if (tx.type === "unlock" && tx.episode != null) {
        const ep = Number(tx.episode);
        if (!episodeUsers.has(ep)) episodeUsers.set(ep, new Set());
        episodeUsers.get(ep)!.add(tx.user_id);
        if (tx.amount < 0) totalUnlockRevenue += Math.abs(tx.amount);
      } else if (tx.type === "full_drama_coin_purchase") {
        fullDramaUsers.add(tx.user_id);
        if (tx.amount < 0) totalUnlockRevenue += Math.abs(tx.amount);
      }
    });

    const episodeUnlockCounts: Record<number, number> = {};
    episodeUsers.forEach((users, ep) => {
      episodeUnlockCounts[ep] = users.size;
    });

    return {
      episodeUnlockCounts,
      fullDramaUnlockCount: fullDramaUsers.size,
      totalUnlockRevenue,
    };
  } catch (error) {
    console.error("Error getting drama episode stats:", error);
    throw error;
  }
}

/**
 * 獲取單個交易記錄
 */
export async function getTransaction(
  id: string
): Promise<Transaction | null> {
  try {
    const transactionRef = doc(db, "transactions", id);
    const transactionSnap = await getDoc(transactionRef);

    if (transactionSnap.exists()) {
      return {
        id: transactionSnap.id,
        ...convertTransactionData(transactionSnap.data(), transactionSnap.id),
      } as Transaction;
    }

    return null;
  } catch (error) {
    console.error("Error getting transaction:", error);
    throw error;
  }
}

